import { useId } from "react";
import { ChevronDownIcon, ChevronRightIcon } from "lucide-react";
import type { ProviderKind } from "@cmmd-forge/contracts";
import { ComboboxList } from "../ui/combobox";
import { ModelListRow } from "./ModelListRow";
import type { PickerModel } from "./useModelPickerModels";
import type { ModelPickerProviderAvailability } from "./modelPickerProviderAvailability";
import type { PickerRailProvider } from "../../cmmd/providerAvailabilityJoin";
import { describeProviderAvailability } from "../../cmmd/providerIneligibilityCopy";
import { isModelPickerNewModel } from "./modelPickerModelHighlights";

export function ModelPickerResults(props: {
  readonly models: ReadonlyArray<PickerModel>;
  readonly favorites: ReadonlySet<string>;
  readonly availability: ReadonlyMap<
    PickerRailProvider,
    ModelPickerProviderAvailability["availability"]
  >;
  readonly isLocked: boolean;
  readonly showLegacySection: boolean;
  readonly legacyStartIndex: number;
  readonly legacyCount: number;
  readonly legacyExpanded: boolean;
  readonly onToggleLegacy: () => void;
  readonly onToggleFavorite: (provider: ProviderKind, slug: string) => void;
}) {
  const legacyId = useId();
  const Chevron = props.legacyExpanded ? ChevronDownIcon : ChevronRightIcon;
  const disclosure = props.showLegacySection ? (
    <button
      type="button"
      aria-label="Legacy models"
      aria-expanded={props.legacyExpanded}
      aria-controls={legacyId}
      onClick={props.onToggleLegacy}
      className="flex min-h-touch w-full items-center gap-2 rounded-md px-3 text-sm text-muted-foreground hover:bg-muted focus-visible:outline-2 focus-visible:outline-ring md:min-h-8 md:text-xs"
    >
      <Chevron aria-hidden="true" className="size-4" />
      <span>Legacy</span>
      <span className="ml-auto tabular-nums">{props.legacyCount}</span>
    </button>
  ) : null;
  const rows = props.models.map((model, index) => {
    const modelKey = `${model.provider}:${model.slug}`;
    const availability = props.availability.get(model.provider);
    const disabled = availability !== undefined && availability.state !== "usable";
    return (
      <ModelListRow
        key={modelKey}
        index={index}
        provider={model.provider}
        model={model}
        isFavorite={props.favorites.has(modelKey)}
        showProvider={!props.isLocked}
        showNewBadge={!model.isLegacy && isModelPickerNewModel(model.provider, model.slug)}
        jumpLabel={index < 9 ? `⌘${index + 1}` : null}
        disabled={disabled}
        disabledReason={
          disabled
            ? (describeProviderAvailability(model.provider, availability) ?? undefined)
            : undefined
        }
        onToggleFavorite={() => props.onToggleFavorite(model.provider, model.slug)}
      />
    );
  });
  return (
    <div className="relative min-h-0 flex-1 bg-background/25">
      <ComboboxList className="model-picker-list size-full divide-y divide-border/45 px-2 py-1.5">
        {props.showLegacySection ? (
          <>
            {rows.slice(0, props.legacyStartIndex)}
            {disclosure}
            <div
              id={legacyId}
              role="group"
              aria-label="Legacy models"
              hidden={!props.legacyExpanded}
              className="divide-y divide-border/45"
            >
              {rows.slice(props.legacyStartIndex)}
            </div>
          </>
        ) : (
          rows
        )}
      </ComboboxList>
    </div>
  );
}
